import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { ReleasesTableV2 } from '@/features/releases/components/ReleasesTableV2';
import { queryReleases } from '@/features/releases/data';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { buildContainsFilter, mergeFilters } from '@/ui/composites/grid/gridFilters';
import {
  releasesTableDefinition,
  releasesSortFieldMap,
  type ReleasesSortDirection,
  type ReleasesSortKey,
} from './releasesTableDefinition';
import type { TagOption } from '@/shared/data/tags';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { ProjectDto, QueryResult, ReleaseListItemDto } from '@flowqa/shared';

type ReleasesTablePanelProps = {
  fixedFilters: GridQueryInput['filters'];
  rangeStartDay: string | null;
  rangeEndDay: string | null;
  noRowsText: string;
  projectOptions?: Array<
    Pick<ProjectDto, 'badgeChars' | 'badgeColor' | 'badgeEnabled' | 'id' | 'name'>
  >;
  searchValue?: string;
  tagOptions?: TagOption[];
};

export const ReleasesTablePanel = ({
  fixedFilters,
  rangeStartDay,
  rangeEndDay,
  noRowsText,
  projectOptions = [],
  searchValue = '',
  tagOptions = [],
}: ReleasesTablePanelProps): ReactElement => {
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const [sortKey, setSortKey] = useState<ReleasesSortKey>(releasesTableDefinition.defaultSortKey);
  const [sortDirection, setSortDirection] = useState<ReleasesSortDirection>(
    releasesTableDefinition.defaultSortDirection,
  );
  const sort = useMemo(
    () => ({ field: sortKey, direction: sortDirection }),
    [sortDirection, sortKey],
  );
  const fixedFiltersKey = useMemo(() => JSON.stringify(fixedFilters), [fixedFilters]);
  const queryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<ReleaseListItemDto>> =>
      queryReleases(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        filters: mergeFilters(input.filters, buildContainsFilter('name', searchValue)),
        sorts: input.sorts.map((inputSort) => ({
          field: releasesSortFieldMap[inputSort.field as ReleasesSortKey],
          direction: inputSort.direction,
        })),
        include: {
          features: { items: true },
          latestTestResults: { counts: true },
        },
      }),
    [searchValue, selectedOrganisationId, selectedProjectId],
  );

  return (
    <ReleasesTableV2
      fixedFilters={fixedFilters}
      queryFn={queryFn}
      queryKey={[
        ...queryKeys.dataGrid(gridPaths.releases),
        selectedOrganisationId ?? null,
        selectedProjectId ?? null,
        sortKey,
        sortDirection,
        rangeStartDay,
        rangeEndDay,
        fixedFiltersKey,
      ]}
      noRowsText={noRowsText}
      projectOptions={projectOptions}
      search={searchValue}
      selectedProjectId={selectedProjectId}
      sort={sort}
      tagOptionsOverride={tagOptions}
      onSortChange={(nextSort) => {
        setSortKey(nextSort.field as ReleasesSortKey);
        setSortDirection(nextSort.direction as ReleasesSortDirection);
      }}
    />
  );
};
